import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchObjectFieldListMetadata } from 'src/actions/jsonArrayTableViewActions';
import { getConsignmentStatusPrettyName } from 'src/utils/consignmentStatus';
import { tripStatusPrettyNames } from 'src/utils/retailConsignment';
import { tmsStatusesPrettyNames } from 'src/components/pages/TMSDashboard/columns';
import i18nInstance from 'src/i18n';

interface ResolvableEvent {
  id?: string;
  isCustom?: boolean;
  prettyName?: string;
  prettyNameOverrides?: Record<string, string>;
}

interface IProps {
  objectType: string;
  status?: string;
  statusList?: string[];
  suffix?: string;
  showSystemEventPrettyName?: boolean;
}

// Resolves the display (pretty) name for a status id for a given object type.
// Resolution order:
//   1. user override for the currently selected language (unless forcing the system name)
//   2. custom event base name (event.prettyName)
//   3. system standard translation (t(getStandardPrettyName))
export const createEventPrettyNameResolver = (
  objectType: string,
  eventMetadata: ResolvableEvent[] | undefined,
  t: (key: string) => string,
  language: string = i18nInstance.language,
) => {
  const eventMap: Record<string, ResolvableEvent> = {};
  if (eventMetadata) {
    for (const event of eventMetadata) {
      if (event?.id) {
        eventMap[event.id.toLowerCase()] = event;
      }
    }
  }

  const getStandardPrettyName = (s: string, suffix?: string, event?: ResolvableEvent): string => {
    switch (objectType) {
      case 'TRIP':
        return tripStatusPrettyNames(s);
      case 'BAG':
        if (suffix) return t(s + suffix);
        return tmsStatusesPrettyNames(s);
      case 'CONSIGNMENT':
        if (suffix) return t(s + suffix);
        return getConsignmentStatusPrettyName(s);
      default:
        return event?.prettyName ? event.prettyName : s;
    }
  };

  return (s?: string, showSystemEventPrettyName = false, suffix?: string): string => {
    if (!s) return '';
    const event = eventMap[s.toLowerCase()];

    if (!showSystemEventPrettyName && language && event?.prettyNameOverrides?.[language]) {
      return event.prettyNameOverrides[language];
    }

    if (event?.isCustom && event?.prettyName) {
      return event.prettyName;
    }

    return t(getStandardPrettyName(s, suffix, event));
  };
};

export const useEventPrettyNameResolver = (objectType: string) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const eventMetadata = useSelector(
    (state: any) => state.jsonArrayTableViewReducer?.eventMetadata?.[objectType],
  );
  const isLoading = useSelector(
    (state: any) => state.jsonArrayTableViewReducer?.isLoading,
  );

  useEffect(() => {
    if (!eventMetadata && !isLoading) {
      dispatch(fetchObjectFieldListMetadata({
        objectType,
        sendCustomFieldsOnly: true,
      }));
    }
  }, [eventMetadata, isLoading, objectType]);

  return useMemo(
    () => createEventPrettyNameResolver(objectType, eventMetadata, t, i18n.language),
    [objectType, eventMetadata, t, i18n.language],
  );
};

const ObjectStatusPrettyName = ({
  objectType,
  status,
  statusList,
  suffix,
  showSystemEventPrettyName = false,
}: IProps) => {
  const resolvePrettyName = useEventPrettyNameResolver(objectType);

  if (statusList) {
    return <>{statusList.map(s => resolvePrettyName(s, showSystemEventPrettyName, suffix)).join(', ')}</>;
  }

  return <>{resolvePrettyName(status, showSystemEventPrettyName, suffix)}</>;
};

export default ObjectStatusPrettyName;
